import React, { useState, useEffect } from 'react';
import wordScrambleService from '../services/wordScrambleService';
import './WordScrambleGame.css';

const WordScrambleGame = () => {
    const [game, setGame] = useState(null);
    const [games, setGames] = useState(null); // For multiple games
    const [currentGameIndex, setCurrentGameIndex] = useState(0); // Track current game in session
    const [letterPool, setLetterPool] = useState([]);
    const [answerSlots, setAnswerSlots] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState({ topic: '', level: 'easy', wordCount: 5 });
    const [userGames, setUserGames] = useState([]);
    const [showGamesList, setShowGamesList] = useState(false);
    const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState(0);

    const topicSuggestions = [
        'động vật', 'trái cây', 'quốc gia', 'đồ vật', 'khoa học',
        'thể thao', 'phim ảnh', 'đồ ăn', 'nghề nghiệp', 'màu sắc',
        'động vật biển', 'món ăn Việt Nam', 'công nghệ', 'xe cộ',
        'nhạc cụ', 'thời tiết', 'gia đình', 'trường học'
    ];

    const levelDisplayNames = {
        'easy': 'Dễ',
        'medium': 'Trung bình',
        'hard': 'Khó'
    };

    // Auto-advance to next game when current game is won
    useEffect(() => {
        if (game && game.status === 'won' && games && games.games.length > 1 && currentGameIndex < games.games.length - 1) {
            setAutoAdvanceCountdown(3);
            const timer = setInterval(() => {
                setAutoAdvanceCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        handleNextGame();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        } else {
            setAutoAdvanceCountdown(0);
        }
    }, [game?.status, games, currentGameIndex]);

    // Reset countdown when manually navigating
    useEffect(() => {
        setAutoAdvanceCountdown(0);
    }, [currentGameIndex]);

    useEffect(() => {
        if (game && game.status === 'in_progress') {
            // Initialize letter pool and answer slots when a new game starts
            const letters = game.scrambledWord.split('').map((char, index) => ({ id: index, letter: char }));
            setLetterPool(letters);
            setAnswerSlots(Array(game.scrambledWord.length).fill(null));
        }
    }, [game]);

    const handleStartGame = async () => {
        // Validation
        if (!options.topic || options.topic.trim() === '') {
            setError('Vui lòng nhập chủ đề');
            return;
        }

        if (options.wordCount < 1 || options.wordCount > 50) {
            setError('Số lượng từ phải từ 1 đến 50');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const result = await wordScrambleService.startGame(options.topic.trim(), options.level, options.wordCount);

            // Always start with the first game
            setGames(result);
            setGame(result.games[0]);
            setCurrentGameIndex(0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectGame = (selectedGame) => {
        setGame(selectedGame);
        setShowGamesList(false); // Hide the games list

        // Find the index of the selected game if games exist
        if (games && games.games) {
            const gameIndex = games.games.findIndex(g => g.gameId === selectedGame.gameId);
            if (gameIndex !== -1) {
                setCurrentGameIndex(gameIndex);
            }
        } else {
            // If coming from user games list, reset games state and index
            setGames(null);
            setCurrentGameIndex(0);
        }
    };

    const handleNextGame = () => {
        if (games && currentGameIndex < games.games.length - 1) {
            const nextIndex = currentGameIndex + 1;
            setCurrentGameIndex(nextIndex);
            setGame(games.games[nextIndex]);
            setAutoAdvanceCountdown(0); // Cancel any ongoing countdown
        }
    };

    const handlePreviousGame = () => {
        if (games && currentGameIndex > 0) {
            const prevIndex = currentGameIndex - 1;
            setCurrentGameIndex(prevIndex);
            setGame(games.games[prevIndex]);
            setAutoAdvanceCountdown(0); // Cancel any ongoing countdown
        }
    };

    const loadUserGames = async () => {
        setLoading(true);
        try {
            const result = await wordScrambleService.getUserGames('in_progress', 20);
            setUserGames(result.games);
            setShowGamesList(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const cancelAutoAdvance = () => {
        setAutoAdvanceCountdown(0);
    };

    const handleLetterClick = (letterObj) => {
        // Find the first empty slot in the answer
        const firstEmptyIndex = answerSlots.findIndex(slot => slot === null);
        if (firstEmptyIndex !== -1) {
            // Move letter from pool to answer slot
            const newAnswerSlots = [...answerSlots];
            newAnswerSlots[firstEmptyIndex] = letterObj;
            setAnswerSlots(newAnswerSlots);

            // Remove letter from pool
            setLetterPool(letterPool.filter(l => l.id !== letterObj.id));
        }
    };

    const handleAnswerSlotClick = (letterObj, index) => {
        if (!letterObj) return; // Clicked on an empty slot

        // Move letter from answer back to pool
        const newAnswerSlots = [...answerSlots];
        newAnswerSlots[index] = null;
        setAnswerSlots(newAnswerSlots);

        // Add letter back to the pool
        setLetterPool([...letterPool, letterObj]);
    };

    const handleGuessSubmit = async (e) => {
        e.preventDefault();
        const currentGuess = answerSlots.map(slot => slot ? slot.letter : '').join('');
        
        if (currentGuess.length !== game.scrambledWord.length) {
            setError('Vui lòng điền đầy đủ các chữ cái.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const updatedGame = await wordScrambleService.makeGuess(game.gameId, currentGuess);
            setGame(updatedGame);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const renderGameContent = () => {
        if (!game) return null;

        if (game.status === 'in_progress') {
            return (
                <div className="scramble-game-area">
                    <div className="scramble-game-info-header">
                        <p className="topic-level-info">Chủ đề: {game.topic} - Cấp độ: {levelDisplayNames[game.level] || game.level}</p>
                        <p className="attempts-info">Số lần đoán còn lại: {game.maxAttempts - game.attempts}</p>
                    </div>
                    
                    <div className="scramble-game-center">
                        <div className="letter-pool">
                            {letterPool.map(letterObj => (
                                <button key={letterObj.id} onClick={() => handleLetterClick(letterObj)} className="letter-tile">
                                    {letterObj.letter}
                                </button>
                            ))}
                        </div>

                        <div className="answer-area">
                            {answerSlots.map((letterObj, index) => (
                                <div key={index} onClick={() => handleAnswerSlotClick(letterObj, index)} className={`answer-slot ${letterObj ? 'filled' : ''}`}>
                                    {letterObj ? letterObj.letter : ''}
                                </div>
                            ))}
                        </div>

                        <button onClick={handleGuessSubmit} disabled={loading} className="play-btn">
                            {loading ? 'Đang kiểm tra...' : 'Kiểm tra đáp án'}
                        </button>

                        <div className="scramble-game-actions">
                            {games && games.games.length > 1 && (
                                <div className="navigation-buttons">
                                    <button
                                        onClick={handlePreviousGame}
                                        className="nav-btn"
                                        disabled={currentGameIndex === 0}
                                    >
                                        ← Từ trước
                                    </button>
                                    <span className="game-counter">
                                        {currentGameIndex + 1} / {games.games.length}
                                    </span>
                                    <button
                                        onClick={handleNextGame}
                                        className="nav-btn"
                                        disabled={currentGameIndex === games.games.length - 1}
                                    >
                                        Từ tiếp →
                                    </button>
                                </div>
                            )}

                            <div className="action-buttons">
                                <button onClick={() => {setGame(null); setGames(null); setShowGamesList(false); setCurrentGameIndex(0);}} className="new-game-btn">
                                    Tạo game mới
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (game.status === 'won' || game.status === 'lost') {
            return (
                <div className={`game-over-message ${game.status === 'won' ? 'status-won' : 'status-lost'}`}>
                    <h3>{game.status === 'won' ? '🎉 Chính xác! Bạn đã thắng! 🎉' : '😢 Sai rồi! Bạn đã thua! 😢'}</h3>

                    {autoAdvanceCountdown > 0 && (
                        <div className="auto-advance-notice">
                            <p>Tự động chuyển sang từ tiếp theo sau <strong>{autoAdvanceCountdown}</strong> giây...</p>
                            <button onClick={cancelAutoAdvance} className="cancel-auto-btn">
                                Hủy tự động chuyển
                            </button>
                        </div>
                    )}

                    <p>Đáp án đúng là: <strong>{game.correctAnswer}</strong></p>

                    <div className="game-over-actions">
                        {games && games.games.length > 1 && (
                            <div className="navigation-buttons">
                                <button
                                    onClick={handlePreviousGame}
                                    className="nav-btn"
                                    disabled={currentGameIndex === 0}
                                >
                                    ← Từ trước
                                </button>
                                <span className="game-counter">
                                    {currentGameIndex + 1} / {games.games.length}
                                </span>
                                <button
                                    onClick={handleNextGame}
                                    className="nav-btn"
                                    disabled={currentGameIndex === games.games.length - 1}
                                >
                                    Từ tiếp →
                                </button>
                            </div>
                        )}

                        <div className="action-buttons">
                            <button onClick={() => {setGame(null); setGames(null); setShowGamesList(false); setCurrentGameIndex(0);}} className="play-again-button">
                                Chơi lại
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="word-scramble-container">
            <div className="word-scramble-content">
                <div style={{width: '100%', maxWidth: '1400px'}}>
                    {error && <p className="error-message">{error}</p>}
                    {!game && !showGamesList ? (
                <div className="game-setup">
                    <div className="form-group">
                        <label htmlFor="topic-input">Chủ đề</label>
                        <input
                            id="topic-input"
                            type="text"
                            value={options.topic}
                            onChange={e => setOptions({...options, topic: e.target.value})}
                            placeholder="VD: động vật, trái cây, công nghệ..."
                            className="topic-input"
                        />
                        <div className="topic-suggestions">
                            <span className="suggestions-label">Gợi ý:</span>
                            {topicSuggestions.slice(0, 6).map(suggestion => (
                                <button
                                    key={suggestion}
                                    type="button"
                                    className="suggestion-btn"
                                    onClick={() => setOptions({...options, topic: suggestion})}
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="form-row">
                        <label htmlFor="level-select">Độ khó</label>
                        <select id="level-select" value={options.level} onChange={e => setOptions({...options, level: e.target.value})}>
                            <option value="easy">Dễ</option>
                            <option value="medium">Trung bình</option>
                            <option value="hard">Khó</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="word-count-input">Số lượng từ</label>
                        <input
                            id="word-count-input"
                            type="number"
                            value={options.wordCount}
                            onChange={e => setOptions({...options, wordCount: Math.max(1, Math.min(50, parseInt(e.target.value) || 1))})}
                            min="1"
                            max="50"
                            className="word-count-input"
                        />
                        <div className="input-hint">
                            Từ 1 đến 50 từ
                        </div>
                    </div>

                    <div className="button-group">
                        <button className="play-btn" onClick={handleStartGame} disabled={loading}>
                            {loading ? 'Đang tạo game...' : `Tạo ${options.wordCount} từ`}
                        </button>

                        <button className="secondary-btn" onClick={loadUserGames} disabled={loading}>
                            {loading ? 'Đang tải...' : 'Xem games đã tạo'}
                        </button>
                    </div>
                </div>
            ) : !game && games ? (
                <div className="games-list">
                    <h3>Chọn từ để chơi ({games.totalCount} từ - {games.topic} - {levelDisplayNames[games.level]})</h3>
                    <div className="games-grid">
                        {games.games.map((gameItem, index) => (
                            <div key={gameItem.gameId} className="game-card" onClick={() => handleSelectGame(gameItem)}>
                                <div className="game-number">#{index + 1}</div>
                                <div className="word-preview">{gameItem.scrambledWord}</div>
                                <div className="game-status">
                                    <span className="attempts">Còn {gameItem.maxAttempts - gameItem.attempts} lượt</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button className="back-btn" onClick={() => {setGames(null); setGame(null);}}>
                        Tạo games mới
                    </button>
                </div>
            ) : !game && showGamesList ? (
                <div className="user-games-list">
                    <h3>Games đang chơi của bạn ({userGames.length})</h3>
                    {userGames.length > 0 ? (
                        <div className="games-grid">
                            {userGames.map((gameItem, index) => (
                                <div key={gameItem.id} className="game-card" onClick={() => handleSelectGame(gameItem)}>
                                    <div className="game-info">
                                        <div className="topic-level">{gameItem.topic} - {levelDisplayNames[gameItem.level]}</div>
                                        <div className="word-preview">{gameItem.scrambledWord}</div>
                                    </div>
                                    <div className="game-status">
                                        <span className="attempts">Còn {gameItem.maxAttempts - gameItem.attempts} lượt</span>
                                        <span className="date">{new Date(gameItem.createdAt).toLocaleDateString('vi-VN')}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>Bạn chưa có game nào đang chơi.</p>
                    )}
                    <button className="back-btn" onClick={() => setShowGamesList(false)}>
                        Tạo game mới
                    </button>
                </div>
            ) : game ? (
                renderGameContent()
            ) : null}
                </div>
            </div>
        </div>
    );
};

export default WordScrambleGame;
